import React, { useState, useEffect } from 'react';
import { clientNoteApi, getMediaUrl, getAuthSession } from '../services/api';

export default function WorksheetFormModal({
  note,
  isTherapist = false,
  onClose,
  onUpdated,
}) {
  const authSession = getAuthSession();
  const ws = note?.worksheet || {};
  const wsDetails = ws.worksheetId || {};

  // Get dynamic questions either from note.worksheet.dynamicFields or populated wsDetails.dynamicFields
  const dynamicFields =
    (Array.isArray(ws.dynamicFields) && ws.dynamicFields.length > 0)
      ? ws.dynamicFields
      : (Array.isArray(wsDetails.dynamicFields) && wsDetails.dynamicFields.length > 0)
      ? wsDetails.dynamicFields
      : [];

  const [responses, setResponses] = useState(() => {
    const existing = ws.formResponses || ws.clientResponses || {};
    return typeof existing === 'object' && existing !== null ? { ...existing } : {};
  });

  const [isSubmitted, setIsSubmitted] = useState(Boolean(ws.isFormSubmitted));
  const [formSubmittedAt, setFormSubmittedAt] = useState(ws.formSubmittedAt || null);
  const [therapistFeedback, setTherapistFeedback] = useState(ws.therapistFeedback || '');
  const [isEditingSubmitted, setIsEditingSubmitted] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [savingFeedback, setSavingFeedback] = useState(false);
  const [notification, setNotification] = useState({ type: '', message: '' });
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    if (ws.formResponses) {
      setResponses(ws.formResponses);
    }
    if (ws.isFormSubmitted !== undefined) {
      setIsSubmitted(Boolean(ws.isFormSubmitted));
    }
    if (ws.formSubmittedAt) {
      setFormSubmittedAt(ws.formSubmittedAt);
    }
    if (ws.therapistFeedback) {
      setTherapistFeedback(ws.therapistFeedback);
    }
  }, [note]);

  // Handle individual response change
  const handleFieldChange = (fieldId, val) => {
    setResponses((prev) => ({
      ...prev,
      [fieldId]: val,
    }));

    // Clear validation error if answered
    if (validationErrors[fieldId]) {
      setValidationErrors((prev) => {
        const copy = { ...prev };
        delete copy[fieldId];
        return copy;
      });
    }
  };

  // Toggle multi-select checkbox option
  const handleCheckboxToggle = (fieldId, optionVal) => {
    setResponses((prev) => {
      const currentList = Array.isArray(prev[fieldId]) ? [...prev[fieldId]] : [];
      const existsIndex = currentList.indexOf(optionVal);
      if (existsIndex > -1) {
        currentList.splice(existsIndex, 1);
      } else {
        currentList.push(optionVal);
      }
      return { ...prev, [fieldId]: currentList };
    });

    if (validationErrors[fieldId]) {
      setValidationErrors((prev) => {
        const copy = { ...prev };
        delete copy[fieldId];
        return copy;
      });
    }
  };

  // Calculate completion progress
  const totalQuestions = dynamicFields.length;
  const answeredCount = dynamicFields.filter((f) => {
    const val = responses[f.id];
    if (Array.isArray(val)) return val.length > 0;
    return val !== undefined && val !== null && String(val).trim() !== '';
  }).length;
  const progressPercent = totalQuestions > 0 ? Math.round((answeredCount / totalQuestions) * 100) : 0;

  // Validate required fields
  const validateForm = () => {
    const errors = {};
    dynamicFields.forEach((f) => {
      if (f.required) {
        const val = responses[f.id];
        if (Array.isArray(val) && val.length === 0) {
          errors[f.id] = 'This field is required';
        } else if (val === undefined || val === null || String(val).trim() === '') {
          errors[f.id] = 'This field is required';
        }
      }
    });
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Save as Draft
  const handleSaveDraft = async () => {
    const targetId =
      note?._id ||
      note?.id ||
      (typeof note?.worksheet?.worksheetId === 'object' ? note?.worksheet?.worksheetId?._id : note?.worksheet?.worksheetId);
    if (!targetId) {
      setNotification({ type: 'error', message: 'Note ID missing' });
      return;
    }

    try {
      setSavingDraft(true);
      setNotification({ type: '', message: '' });
      let res;
      try {
        res = await clientNoteApi.submitWorksheetForm(targetId, {
          formResponses: responses,
          isSubmitted: false,
          isDraft: true,
        });
      } catch (submitErr) {
        // Fallback to syncLiveWorksheet endpoint
        res = await clientNoteApi.syncLiveWorksheet(targetId, {
          formResponses: responses,
          clientResponses: responses,
          isSubmitted: false,
          isDraft: true,
        });
      }
      setNotification({ type: 'success', message: '💾 Responses saved as draft!' });
      if (onUpdated && res?.note) onUpdated(res.note);
    } catch (err) {
      setNotification({ type: 'error', message: err.message || 'Failed to save draft' });
    } finally {
      setSavingDraft(false);
    }
  };

  // Final Form Submission by Client
  const handleSubmitForm = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      setNotification({ type: 'error', message: 'Please answer all required questions before submitting.' });
      return;
    }

    const targetId =
      note?._id ||
      note?.id ||
      (typeof note?.worksheet?.worksheetId === 'object' ? note?.worksheet?.worksheetId?._id : note?.worksheet?.worksheetId);
    if (!targetId) {
      setNotification({ type: 'error', message: 'Note ID missing' });
      return;
    }

    try {
      setSubmitting(true);
      setNotification({ type: '', message: '' });
      let res;
      try {
        res = await clientNoteApi.submitWorksheetForm(targetId, {
          formResponses: responses,
          isSubmitted: true,
          isDraft: false,
        });
      } catch (submitErr) {
        // Fallback to syncLiveWorksheet endpoint
        res = await clientNoteApi.syncLiveWorksheet(targetId, {
          formResponses: responses,
          clientResponses: responses,
          isSubmitted: true,
          isFormSubmitted: true,
          isDraft: false,
        });
      }

      setIsSubmitted(true);
      setFormSubmittedAt(new Date());
      setIsEditingSubmitted(false);
      setNotification({
        type: 'success',
        message: '🎉 Worksheet form submitted successfully! Your therapist can now view your responses.',
      });
      if (onUpdated && res?.note) onUpdated(res.note);
    } catch (err) {
      setNotification({ type: 'error', message: err.message || 'Failed to submit form' });
    } finally {
      setSubmitting(false);
    }
  };

  // Therapist Saves Clinical Evaluation Feedback
  const handleSaveFeedback = async () => {
    const targetId =
      note?._id ||
      note?.id ||
      (typeof note?.worksheet?.worksheetId === 'object' ? note?.worksheet?.worksheetId?._id : note?.worksheet?.worksheetId);
    if (!targetId) return;

    try {
      setSavingFeedback(true);
      setNotification({ type: '', message: '' });
      let res;
      try {
        res = await clientNoteApi.saveTherapistFeedback(targetId, {
          feedback: therapistFeedback,
        });
      } catch (fbErr) {
        res = await clientNoteApi.syncLiveWorksheet(targetId, {
          therapistFeedback,
        });
      }
      setNotification({ type: 'success', message: '✅ Clinical feedback saved successfully!' });
      if (onUpdated && res?.note) onUpdated(res.note);
    } catch (err) {
      setNotification({ type: 'error', message: err.message || 'Failed to save feedback' });
    } finally {
      setSavingFeedback(false);
    }
  };

  // Render field input
  const renderFieldInput = (f, index) => {
    const value = responses[f.id] !== undefined ? responses[f.id] : '';
    const readOnly = isTherapist || (isSubmitted && !isEditingSubmitted);
    const hasError = Boolean(validationErrors[f.id]);

    switch (f.fieldType) {
      case 'textarea':
        return (
          <textarea
            disabled={readOnly}
            rows={4}
            value={value}
            placeholder={readOnly ? 'No response provided.' : f.placeholder || 'Type your reflection or answer here...'}
            onChange={(e) => handleFieldChange(f.id, e.target.value)}
            style={{
              width: '100%',
              padding: '12px 14px',
              borderRadius: '10px',
              border: hasError ? '2px solid #ef4444' : '1px solid #cbd5e1',
              fontSize: '14px',
              lineHeight: '1.6',
              background: readOnly ? '#f8fafc' : '#ffffff',
              color: '#1e293b',
              outline: 'none',
              resize: 'vertical',
              boxSizing: 'border-box',
              fontFamily: 'inherit',
            }}
          />
        );

      case 'textbox':
        return (
          <input
            type="text"
            disabled={readOnly}
            value={value}
            placeholder={readOnly ? 'No response provided.' : f.placeholder || 'Your answer...'}
            onChange={(e) => handleFieldChange(f.id, e.target.value)}
            style={{
              width: '100%',
              padding: '10px 14px',
              borderRadius: '8px',
              border: hasError ? '2px solid #ef4444' : '1px solid #cbd5e1',
              fontSize: '14px',
              background: readOnly ? '#f8fafc' : '#ffffff',
              color: '#1e293b',
              outline: 'none',
              boxSizing: 'border-box',
            }}
          />
        );

      case 'number':
        return (
          <input
            type="number"
            disabled={readOnly}
            value={value}
            placeholder={f.placeholder || 'Enter numeric score or value...'}
            onChange={(e) => handleFieldChange(f.id, e.target.value)}
            style={{
              width: '200px',
              padding: '10px 14px',
              borderRadius: '8px',
              border: hasError ? '2px solid #ef4444' : '1px solid #cbd5e1',
              fontSize: '14px',
              background: readOnly ? '#f8fafc' : '#ffffff',
              color: '#1e293b',
              outline: 'none',
            }}
          />
        );

      case 'date':
        return (
          <input
            type="date"
            disabled={readOnly}
            value={value}
            onChange={(e) => handleFieldChange(f.id, e.target.value)}
            style={{
              padding: '10px 14px',
              borderRadius: '8px',
              border: hasError ? '2px solid #ef4444' : '1px solid #cbd5e1',
              fontSize: '14px',
              background: readOnly ? '#f8fafc' : '#ffffff',
              color: '#1e293b',
              outline: 'none',
            }}
          />
        );

      case 'scale': {
        const scaleVal = Number(value);
        return (
          <div>
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '8px' }}>
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => {
                const isSelected = scaleVal === num;
                // Color scale from gentle emerald to intense rose
                const colorMap = [
                  '#10b981', '#10b981', '#059669', '#14b8a6', '#0284c7',
                  '#6366f1', '#8b5cf6', '#d97706', '#f97316', '#ef4444',
                ];
                const activeColor = colorMap[num - 1] || '#6366f1';

                return (
                  <button
                    key={num}
                    type="button"
                    disabled={readOnly}
                    onClick={() => handleFieldChange(f.id, num)}
                    style={{
                      width: '42px',
                      height: '42px',
                      borderRadius: '10px',
                      border: isSelected ? `2px solid ${activeColor}` : '1px solid #e2e8f0',
                      background: isSelected ? activeColor : '#ffffff',
                      color: isSelected ? '#ffffff' : '#334155',
                      fontWeight: 700,
                      fontSize: '14px',
                      cursor: readOnly ? 'default' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: isSelected ? `0 4px 12px ${activeColor}55` : 'none',
                      transition: 'all 0.15s ease',
                    }}
                  >
                    {num}
                  </button>
                );
              })}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11.5px', color: '#64748b', maxWidth: '440px' }}>
              <span>1 = Mild / Very Low</span>
              <span>5 = Moderate</span>
              <span>10 = Severe / Maximum</span>
            </div>
          </div>
        );
      }

      case 'radio':
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {(f.options || []).map((opt, optIdx) => {
              const isSelected = value === opt;
              return (
                <label
                  key={optIdx}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '10px 14px',
                    borderRadius: '8px',
                    background: isSelected ? '#f5f3ff' : '#ffffff',
                    border: isSelected ? '1.5px solid #8b5cf6' : '1px solid #e2e8f0',
                    cursor: readOnly ? 'default' : 'pointer',
                    fontSize: '13.5px',
                    color: isSelected ? '#5b21b6' : '#334155',
                    fontWeight: isSelected ? 600 : 400,
                    transition: 'all 0.15s',
                  }}
                >
                  <input
                    type="radio"
                    name={f.id}
                    disabled={readOnly}
                    checked={isSelected}
                    onChange={() => handleFieldChange(f.id, opt)}
                    style={{ accentColor: '#7c3aed', cursor: readOnly ? 'default' : 'pointer' }}
                  />
                  <span>{opt}</span>
                </label>
              );
            })}
          </div>
        );

      case 'checkbox': {
        const selectedList = Array.isArray(value) ? value : [];
        return (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {(f.options || []).map((opt, optIdx) => {
              const isSelected = selectedList.includes(opt);
              return (
                <button
                  key={optIdx}
                  type="button"
                  disabled={readOnly}
                  onClick={() => handleCheckboxToggle(f.id, opt)}
                  style={{
                    padding: '8px 14px',
                    borderRadius: '20px',
                    border: isSelected ? '1.5px solid #7c3aed' : '1px solid #cbd5e1',
                    background: isSelected ? '#7c3aed' : '#ffffff',
                    color: isSelected ? '#ffffff' : '#334155',
                    fontSize: '13px',
                    fontWeight: isSelected ? 600 : 500,
                    cursor: readOnly ? 'default' : 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    transition: 'all 0.15s',
                  }}
                >
                  <span>{isSelected ? '✓' : '+'}</span>
                  <span>{opt}</span>
                </button>
              );
            })}
          </div>
        );
      }

      case 'select':
        return (
          <select
            disabled={readOnly}
            value={value}
            onChange={(e) => handleFieldChange(f.id, e.target.value)}
            style={{
              width: '100%',
              maxWidth: '380px',
              padding: '10px 14px',
              borderRadius: '8px',
              border: hasError ? '2px solid #ef4444' : '1px solid #cbd5e1',
              fontSize: '14px',
              background: readOnly ? '#f8fafc' : '#ffffff',
              color: '#1e293b',
              outline: 'none',
            }}
          >
            <option value="">-- Select an option --</option>
            {(f.options || []).map((opt, optIdx) => (
              <option key={optIdx} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        );

      default:
        return (
          <input
            type="text"
            disabled={readOnly}
            value={value}
            placeholder={f.placeholder || 'Answer...'}
            onChange={(e) => handleFieldChange(f.id, e.target.value)}
            style={{
              width: '100%',
              padding: '10px 14px',
              borderRadius: '8px',
              border: hasError ? '2px solid #ef4444' : '1px solid #cbd5e1',
              fontSize: '14px',
              background: readOnly ? '#f8fafc' : '#ffffff',
            }}
          />
        );
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(15, 23, 42, 0.75)',
        zIndex: 999999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        backdropFilter: 'blur(5px)',
      }}
    >
      <div
        style={{
          background: '#ffffff',
          borderRadius: '20px',
          width: '100%',
          maxWidth: '860px',
          maxHeight: '92vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          overflow: 'hidden',
          border: '1px solid #e2e8f0',
        }}
      >
        {/* MODAL HEADER */}
        <div
          style={{
            padding: '22px 28px',
            background: isTherapist
              ? 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)'
              : 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
            color: '#ffffff',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '12px',
          }}
        >
          <div style={{ flex: 1, minWidth: '260px' }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'rgba(255,255,255,0.18)', padding: '4px 10px', borderRadius: '16px', fontSize: '12px', fontWeight: 700, marginBottom: '6px' }}>
              {isTherapist ? '👨‍⚕️ Clinician Worksheet Review' : '📝 Interactive Client Worksheet Form'}
            </div>
            <h2 style={{ margin: '0 0 4px 0', fontSize: '22px', fontWeight: 800 }}>
              {ws.title || wsDetails.title || 'Worksheet Form'}
            </h2>
            <p style={{ margin: 0, fontSize: '13px', opacity: 0.9 }}>
              {isTherapist
                ? `Client: ${note.clientName || 'Assigned Client'} • Assigned on ${note.createdAt ? new Date(note.createdAt).toLocaleDateString() : 'Recent'}`
                : `Assigned by Dr. ${note.physiotherapistId?.name || 'Your Therapist'}`}
            </p>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {/* Status Badge */}
            <span
              style={{
                fontSize: '12.5px',
                fontWeight: 700,
                padding: '6px 14px',
                borderRadius: '20px',
                background: isSubmitted ? '#dcfce7' : '#fef3c7',
                color: isSubmitted ? '#15803d' : '#b45309',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              {isSubmitted ? '🟢 Submitted' : '⏳ Response Pending'}
            </span>

            <button
              type="button"
              onClick={onClose}
              style={{
                background: 'rgba(255,255,255,0.2)',
                border: 'none',
                color: '#ffffff',
                width: '36px',
                height: '36px',
                borderRadius: '50%',
                fontSize: '18px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'background 0.2s',
              }}
            >
              ✕
            </button>
          </div>
        </div>

        {/* SUBMISSION / STATUS BANNER */}
        <div
          style={{
            background: isSubmitted ? '#f0fdf4' : '#f8fafc',
            borderBottom: '1px solid #e2e8f0',
            padding: '12px 28px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '10px',
            fontSize: '13px',
          }}
        >
          {isSubmitted ? (
            <div style={{ color: '#166534', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '18px' }}>✅</span>
              <span>
                <strong>Form Completed & Submitted:</strong>{' '}
                {formSubmittedAt ? new Date(formSubmittedAt).toLocaleString() : 'Recently submitted'}
              </span>
            </div>
          ) : (
            <div style={{ color: '#475569', display: 'flex', alignItems: 'center', gap: '10px', flex: 1 }}>
              <span>
                Progress: <strong>{answeredCount}</strong> of <strong>{totalQuestions}</strong> questions answered
              </span>
              <div style={{ width: '140px', height: '8px', background: '#e2e8f0', borderRadius: '4px', overflow: 'hidden' }}>
                <div
                  style={{
                    width: `${progressPercent}%`,
                    height: '100%',
                    background: progressPercent === 100 ? '#10b981' : '#6366f1',
                    transition: 'width 0.3s',
                  }}
                />
              </div>
              <span style={{ fontWeight: 700, color: '#6366f1' }}>{progressPercent}%</span>
            </div>
          )}

          <div style={{ display: 'flex', gap: '8px' }}>
            {/* Download / Print Form Summary */}
            <button
              type="button"
              onClick={() => window.print()}
              style={{
                background: '#ffffff',
                border: '1px solid #cbd5e1',
                padding: '6px 12px',
                borderRadius: '6px',
                fontSize: '12.5px',
                fontWeight: 600,
                color: '#475569',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
              }}
            >
              🖨️ Print / Export
            </button>

            {/* Client Edit Responses Toggle */}
            {!isTherapist && isSubmitted && !isEditingSubmitted && (
              <button
                type="button"
                onClick={() => setIsEditingSubmitted(true)}
                style={{
                  background: '#6366f1',
                  border: 'none',
                  padding: '6px 12px',
                  borderRadius: '6px',
                  fontSize: '12.5px',
                  fontWeight: 600,
                  color: '#ffffff',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                }}
              >
                ✏️ Edit My Responses
              </button>
            )}
          </div>
        </div>

        {/* NOTIFICATION MESSAGE */}
        {notification.message && (
          <div
            style={{
              padding: '12px 28px',
              background: notification.type === 'error' ? '#fef2f2' : '#f0fdf4',
              color: notification.type === 'error' ? '#b91c1c' : '#15803d',
              borderBottom: '1px solid #e2e8f0',
              fontSize: '13.5px',
              fontWeight: 600,
            }}
          >
            {notification.type === 'error' ? '⚠️ ' : '✓ '}
            {notification.message}
          </div>
        )}

        {/* MODAL BODY (SCROLLABLE) */}
        <div style={{ padding: '28px', overflowY: 'auto', flex: 1 }}>
          {/* Session Note / Context from Therapist */}
          {note.note && (
            <div
              style={{
                background: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: '12px',
                padding: '16px 20px',
                marginBottom: '24px',
              }}
            >
              <div style={{ fontSize: '13px', fontWeight: 700, color: '#0f172a', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                📋 Session Note / Clinical Instructions:
              </div>
              <div style={{ fontSize: '13.5px', color: '#475569', lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>
                {note.note}
              </div>
            </div>
          )}

          {/* Reference PDF file link if attached */}
          {ws.fileUrl && (
            <div
              style={{
                background: '#eff6ff',
                border: '1px solid #bfdbfe',
                borderRadius: '10px',
                padding: '12px 18px',
                marginBottom: '24px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '10px',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#1e40af' }}>
                <span>📎</span>
                <span>Reference Worksheet Guide: <strong>{ws.fileName || 'Worksheet Document'}</strong></span>
              </div>
              <a
                href={getMediaUrl(ws.fileUrl)}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  background: '#2563eb',
                  color: '#ffffff',
                  padding: '6px 14px',
                  borderRadius: '6px',
                  textDecoration: 'none',
                  fontSize: '12.5px',
                  fontWeight: 600,
                }}
              >
                📥 View Reference Guide
              </a>
            </div>
          )}

          {/* DYNAMIC QUESTIONS FORM */}
          {dynamicFields.length > 0 ? (
            <form onSubmit={handleSubmitForm}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '22px' }}>
                {dynamicFields.map((field, idx) => {
                  const hasError = Boolean(validationErrors[field.id]);
                  return (
                    <div
                      key={field.id || idx}
                      style={{
                        background: '#ffffff',
                        border: hasError ? '1.5px solid #ef4444' : '1px solid #e2e8f0',
                        borderRadius: '14px',
                        padding: '20px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.02)',
                        transition: 'border-color 0.2s',
                      }}
                    >
                      {/* Question Label */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px' }}>
                        <label
                          style={{
                            fontSize: '15px',
                            fontWeight: 700,
                            color: '#0f172a',
                            lineHeight: '1.4',
                          }}
                        >
                          <span style={{ color: '#6366f1', marginRight: '6px' }}>{idx + 1}.</span>
                          {field.question}
                          {field.required && <span style={{ color: '#ef4444', marginLeft: '4px' }}>*</span>}
                        </label>

                        {field.fieldType && (
                          <span
                            style={{
                              fontSize: '11px',
                              padding: '2px 8px',
                              borderRadius: '12px',
                              background: '#f1f5f9',
                              color: '#64748b',
                              fontWeight: 600,
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px',
                            }}
                          >
                            {field.fieldType}
                          </span>
                        )}
                      </div>

                      {/* Help Text / Clinical Guidance */}
                      {field.helpText && (
                        <p style={{ margin: '0 0 12px 0', fontSize: '12.5px', color: '#64748b', lineHeight: '1.5' }}>
                          💡 {field.helpText}
                        </p>
                      )}

                      {/* Input Control */}
                      <div style={{ marginTop: '8px' }}>
                        {renderFieldInput(field, idx)}
                      </div>

                      {/* Validation Error Message */}
                      {hasError && (
                        <p style={{ margin: '6px 0 0 0', fontSize: '12px', color: '#ef4444', fontWeight: 600 }}>
                          ⚠️ {validationErrors[field.id]}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* CLIENT ACTION BUTTONS */}
              {!isTherapist && (!isSubmitted || isEditingSubmitted) && (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: '12px',
                    marginTop: '28px',
                    paddingTop: '20px',
                    borderTop: '1px solid #e2e8f0',
                  }}
                >
                  <button
                    type="button"
                    disabled={savingDraft || submitting}
                    onClick={handleSaveDraft}
                    style={{
                      padding: '11px 22px',
                      background: '#f1f5f9',
                      color: '#475569',
                      border: '1px solid #cbd5e1',
                      borderRadius: '10px',
                      fontWeight: 600,
                      fontSize: '14px',
                      cursor: 'pointer',
                    }}
                  >
                    {savingDraft ? 'Saving Draft...' : '💾 Save Draft'}
                  </button>

                  <button
                    type="submit"
                    disabled={submitting || savingDraft}
                    style={{
                      padding: '11px 26px',
                      background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: '10px',
                      fontWeight: 700,
                      fontSize: '14px',
                      cursor: 'pointer',
                      boxShadow: '0 4px 14px rgba(79, 70, 229, 0.35)',
                    }}
                  >
                    {submitting ? 'Submitting to Therapist...' : '🚀 Submit Worksheet to Therapist'}
                  </button>
                </div>
              )}
            </form>
          ) : (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: '#64748b' }}>
              <div style={{ fontSize: '36px', marginBottom: '10px' }}>📄</div>
              <h4>No dynamic form questions found for this worksheet template.</h4>
              <p style={{ fontSize: '13.5px' }}>
                Please check with your clinician or view the reference guide document.
              </p>
            </div>
          )}

          {/* THERAPIST CLINICAL FEEDBACK & EVALUATION SECTION */}
          <div
            style={{
              marginTop: '36px',
              padding: '24px',
              background: '#f8fafc',
              borderRadius: '16px',
              border: '1.5px solid #cbd5e1',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 800, color: '#0f172a', display: 'flex', alignItems: 'center', gap: '8px' }}>
                💬 Clinician Feedback & Evaluation
              </h3>
              {note.worksheet?.status === 'reviewed' && (
                <span style={{ fontSize: '11.5px', background: '#dcfce7', color: '#166534', padding: '3px 10px', borderRadius: '12px', fontWeight: 700 }}>
                  ✓ Feedback Shared
                </span>
              )}
            </div>

            <p style={{ margin: '0 0 12px 0', fontSize: '12.5px', color: '#64748b' }}>
              {isTherapist
                ? 'Provide therapeutic observations, encouragement, or an action plan based on the client’s responses above. The client can view your feedback directly.'
                : 'Comments, guidance, and evaluation provided by your clinician:'}
            </p>

            {isTherapist ? (
              <div>
                <textarea
                  rows={4}
                  value={therapistFeedback}
                  onChange={(e) => setTherapistFeedback(e.target.value)}
                  placeholder="Enter clinical observations, insights, cognitive reframes, or next session action items..."
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    borderRadius: '10px',
                    border: '1px solid #cbd5e1',
                    fontSize: '14px',
                    lineHeight: '1.6',
                    background: '#ffffff',
                    color: '#1e293b',
                    outline: 'none',
                    boxSizing: 'border-box',
                    fontFamily: 'inherit',
                    marginBottom: '12px',
                  }}
                />
                <button
                  type="button"
                  disabled={savingFeedback}
                  onClick={handleSaveFeedback}
                  style={{
                    padding: '9px 20px',
                    background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: 700,
                    fontSize: '13.5px',
                    cursor: 'pointer',
                    boxShadow: '0 3px 10px rgba(16,185,129,0.3)',
                  }}
                >
                  {savingFeedback ? 'Saving Feedback...' : '💾 Save & Share Clinical Feedback'}
                </button>
              </div>
            ) : (
              <div
                style={{
                  background: '#ffffff',
                  padding: '14px 18px',
                  borderRadius: '10px',
                  border: '1px solid #e2e8f0',
                  fontSize: '14px',
                  lineHeight: '1.6',
                  color: therapistFeedback ? '#1e293b' : '#94a3b8',
                  fontStyle: therapistFeedback ? 'normal' : 'italic',
                }}
              >
                {therapistFeedback || 'Your therapist has not provided written feedback on this submission yet.'}
              </div>
            )}
          </div>
        </div>

        {/* MODAL FOOTER */}
        <div
          style={{
            padding: '14px 28px',
            background: '#ffffff',
            borderTop: '1px solid #e2e8f0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div style={{ fontSize: '12.5px', color: '#64748b' }}>
            {isTherapist
              ? `Viewing responses for ${note.clientName || 'Client'}`
              : isSubmitted
              ? 'Your completed responses have been logged with your therapist.'
              : 'Be honest and reflective. Your answers are private to you and your therapist.'}
          </div>

          <button
            type="button"
            onClick={onClose}
            style={{
              padding: '8px 20px',
              background: '#f1f5f9',
              border: 'none',
              borderRadius: '8px',
              color: '#475569',
              fontWeight: 600,
              cursor: 'pointer',
              fontSize: '13.5px',
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
